import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../services/api';
import {
  Database, Download, Trash2, Upload, Plus,
  AlertTriangle, CheckCircle, Clock, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';

const FMT_DATE = (d) => new Date(d).toLocaleString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: true
});

// ── Confirm Dialog ─────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel, danger }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
    <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${danger ? 'bg-red-50' : 'bg-amber-50'}`}>
        <AlertTriangle size={24} className={danger ? 'text-red-500' : 'text-amber-500'} />
      </div>
      <p className="text-sm text-gray-700 text-center mb-6">{message}</p>
      <div className="flex gap-3">
        <button onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button onClick={onConfirm}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}`}>
          Confirm
        </button>
      </div>
    </div>
  </div>
);

const Backup = () => {
  const qc              = useQueryClient();
  const fileRef         = useRef();
  const [confirm, setConfirm]   = useState(null); // { type, payload, message }
  const [restoring, setRestoring] = useState(false);

  // ── Queries ───────────────────────────────
  const { data: backups = [], isLoading, refetch } = useQuery({
    queryKey: ['backups'],
    queryFn : () => API.get('/backup/list').then(r => r.data.data)
  });

  // ── Mutations ─────────────────────────────
  const createMutation = useMutation({
    mutationFn: () => API.post('/backup/create'),
    onSuccess : (res) => {
      toast.success(`Backup created: ${res.data.data.filename}`);
      qc.invalidateQueries(['backups']);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Backup failed')
  });

  const deleteMutation = useMutation({
    mutationFn: (filename) => API.delete(`/backup/${filename}`),
    onSuccess : () => {
      toast.success('Backup deleted');
      qc.invalidateQueries(['backups']);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Delete failed')
  });

  // ── Download ──────────────────────────────
  const handleDownload = async (filename) => {
    try {
      const res = await API.get(`/backup/download/${filename}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch { toast.error('Download failed'); }
  };

  // ── Restore ───────────────────────────────
  const handleRestoreFile = async (file) => {
    if (!file) return;
    setConfirm({
      type   : 'restore',
      payload: file,
      message: `⚠️ This will OVERWRITE the entire database with "${file.name}". All current data will be replaced. This cannot be undone. Are you absolutely sure?`,
      danger : true
    });
  };

  const doRestore = async (file) => {
    setRestoring(true);
    try {
      const form = new FormData();
      form.append('backup_file', file);
      await API.post('/backup/restore', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      toast.success('Database restored successfully');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Restore failed');
    } finally {
      setRestoring(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ── Confirm handler ───────────────────────
  const handleConfirm = () => {
    const c = confirm;
    setConfirm(null);
    if (c.type === 'delete')  deleteMutation.mutate(c.payload);
    if (c.type === 'restore') doRestore(c.payload);
    if (c.type === 'create')  createMutation.mutate();
  };

  const totalSize = backups.reduce((s, b) => s + b.size, 0);
  const formatBytes = (b) => b < 1024 * 1024 ? (b / 1024).toFixed(1) + ' KB' : (b / (1024 * 1024)).toFixed(1) + ' MB';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Database Backup & Restore</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage database backups — super admin only</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => refetch()}
            className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={() => setConfirm({ type: 'create', message: 'Create a new database backup now?', danger: false })}
            disabled={createMutation.isPending}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            <Plus size={14} />
            {createMutation.isPending ? 'Creating...' : 'Create Backup Now'}
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Backups',   value: backups.length,         icon: Database,  color: 'text-blue-600',  bg: 'bg-blue-50'  },
          { label: 'Total Size',      value: formatBytes(totalSize),  icon: Database,  color: 'text-gray-700',  bg: 'bg-gray-50'  },
          { label: 'Latest Backup',   value: backups[0] ? FMT_DATE(backups[0].created_at) : 'None', icon: Clock, color: 'text-green-700', bg: 'bg-green-50' }
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center mb-3`}>
              <Icon size={18} className={color} />
            </div>
            <p className="text-xs text-gray-400">{label}</p>
            <p className={`text-sm font-semibold mt-0.5 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Auto backup info */}
      <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3 flex items-center gap-3">
        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
        <p className="text-sm text-green-700">
          <strong>Auto-backup active</strong> — Daily at 2:00 AM · Last 7 days retained · Stored at <code className="text-xs bg-green-100 px-1 rounded">/var/backups/manpower/</code>
        </p>
      </div>

      {/* Backups table */}
      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700">Available Backups</h2>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading backups...</div>
        ) : !backups.length ? (
          <div className="text-center py-12">
            <Database size={28} className="mx-auto text-gray-200 mb-3" />
            <p className="text-sm text-gray-400">No backups yet. Create one now.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Filename</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Created</th>
                <th className="text-right text-xs font-medium text-gray-400 px-5 py-3">Size</th>
                <th className="text-right text-xs font-medium text-gray-400 px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {backups.map((b, i) => (
                <tr key={b.filename} className="hover:bg-gray-50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Database size={14} className="text-blue-400 flex-shrink-0" />
                      <span className="font-mono text-xs text-gray-700">{b.filename}</span>
                      {i === 0 && (
                        <span className="bg-green-50 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">Latest</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-500">{FMT_DATE(b.created_at)}</td>
                  <td className="px-5 py-3 text-right text-xs text-gray-600 font-mono">{b.size_human}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleDownload(b.filename)}
                        className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium">
                        <Download size={13} /> Download
                      </button>
                      <button
                        onClick={() => setConfirm({
                          type   : 'delete',
                          payload: b.filename,
                          message: `Delete backup "${b.filename}"? This cannot be undone.`,
                          danger : true
                        })}
                        className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 font-medium">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Restore section */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <Upload size={18} className="text-red-500" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Restore from Backup</h2>
            <p className="text-xs text-gray-400 mt-0.5">Upload a .sql.gz backup file to restore the database. <strong className="text-red-500">This will overwrite all current data.</strong></p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
          <AlertTriangle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-600">
            Restoring will <strong>permanently replace</strong> all existing data. Make sure you have a fresh backup before restoring. This action cannot be undone.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".sql.gz,.sql"
            onChange={(e) => handleRestoreFile(e.target.files[0])}
            disabled={restoring}
            className="block text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-red-50 file:text-red-700 hover:file:bg-red-100 disabled:opacity-50"
          />
          {restoring && (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <RefreshCw size={14} className="animate-spin" />
              Restoring database...
            </div>
          )}
        </div>
      </div>

      {/* Confirm dialog */}
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          danger={confirm.danger}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default Backup;
